import { Alert, Button, Group, Skeleton, Stack, Text } from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'
import type { ReactNode } from 'react'

/**
 * Decide entre carregando, erro e vazio para uma regiao alimentada por useQuery.
 *
 * Existe porque o padrao `data?.items ?? []` colapsa "a API respondeu lista vazia" e "a API nao
 * respondeu" no mesmo `[]`, e `[]` tem um texto so — a tela afirma que o dado nao existe quando o
 * que nao existe e a resposta. Medido em 29 telas
 * (docs/superpowers/plans/2026-08-13-erro-como-vazio.md).
 *
 * Nao adivinha o que e "vazio": quem chama sabe o formato do proprio dado e passa `vazio`. O que
 * este componente garante e a ORDEM, que e onde o defeito mora.
 */
export interface ConsultaMinima {
  isPending: boolean
  isError: boolean
  error?: unknown
  data?: unknown
  refetch?: () => unknown
}

export interface EstadoDeConsultaProps {
  consulta: ConsultaMinima
  /**
   * Falha que a consulta NAO enxerga: a resposta veio com HTTP 200 e o erro esta no corpo.
   *
   * Acontece de verdade — a aba Performance de /banco-de-dados le `error` de dentro do payload,
   * e antes disto respondia com `null`, apagando a secao inteira sem dizer nada. Erro no corpo E
   * a resposta, entao nao ha conteudo bom a preservar: entra no mesmo ramo do erro de rede.
   */
  falhou?: boolean
  /** Verdadeiro quando a consulta respondeu e nao veio nada. Quem chama calcula. */
  vazio?: boolean
  estadoVazio?: ReactNode
  carregando?: ReactNode
  children: ReactNode
}

export function EstadoDeConsulta({
  consulta,
  falhou = false,
  vazio = false,
  estadoVazio,
  carregando,
  children,
}: EstadoDeConsultaProps) {
  if (consulta.isPending) {
    return <>{carregando ?? <Skeleton height={36} radius="sm" />}</>
  }

  // Erro so substitui conteudo quando NAO HA conteudo. Recarga em segundo plano que falha nao
  // pode apagar dado bom da tela — o usuario estava lendo aquilo.
  if ((consulta.isError && consulta.data === undefined) || falhou) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        title="Não foi possível carregar"
        color="red"
        variant="light"
      >
        <Stack gap="xs" align="flex-start">
          {/*
            Sem detalhe tecnico. A mensagem do erro traz host, porta e driver, que nao ajudam
            quem le e ajudam quem nao deveria ver.
          */}
          <Text size="sm">
            A consulta falhou. Isto não significa que não existam registros — significa que não
            deu para saber.
          </Text>
          {consulta.refetch && (
            <Group>
              <Button size="xs" variant="default" onClick={() => consulta.refetch?.()}>
                Tentar novamente
              </Button>
            </Group>
          )}
        </Stack>
      </Alert>
    )
  }

  if (vazio) return <>{estadoVazio}</>

  return <>{children}</>
}
